import { useEffect, useState } from 'react';

import type { Stripe } from '@stripe/stripe-js';

import { getAppGlobals } from '~/components/AppGlobals/utils';
import type { Publication } from '~/interfaces/publication';

interface Props {
  publication: Publication;
  shouldLoadStripe?: boolean; // Control when to actually load Stripe
}

const useStripeSession = ({ publication, shouldLoadStripe = true }: Props) => {
  const [isLoadingStripeSession, setIsLoadingStripeSession] = useState(true);
  const [stripeSession, setStripeSession] = useState<Stripe | null>(null);

  const getAndSetStripeSession = async () => {
    const appGlobals = getAppGlobals();
    try {
      setIsLoadingStripeSession(true);

      // Dynamic import - only loads Stripe when this function is called
      const { loadStripe } = await import('@stripe/stripe-js');

      const session = await loadStripe(appGlobals.STRIPE_PUBLISHABLE_KEY, {
        stripeAccount: publication.stripe_user_id ?? '',
      });
      setStripeSession(session);
    } catch (error: unknown) {
      // todo: handle stripe session error
      if (error instanceof Error) {
        console.error(error.message);
      }
    }

    setIsLoadingStripeSession(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Keeping for backwards compatibility
  useEffect(() => {
    if (!stripeSession && window && shouldLoadStripe) {
      getAndSetStripeSession();
    }
  }, [stripeSession, shouldLoadStripe]);

  return {
    isLoadingStripeSession,
    stripeSession,
  };
};

export default useStripeSession;
